import React, { useEffect, useState, useCallback } from "react";
import {
  MdBarChart, MdAttachMoney, MdShoppingCart, MdStar,
  MdTimer, MdTrendingUp, MdTrendingDown, MdLightbulb,
  MdCategory, MdRestaurantMenu,
} from "react-icons/md";
import Widget from "components/widget/Widget";
import Card from "components/card";
import BarChart from "components/charts/BarChart";
import { useAuth } from "contexts/AuthContext";
import { getDashboardStats, getMenuAnalytics } from "services/statsService";

const PERIODS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week",  label: "Cette semaine" },
  { key: "month", label: "Ce mois" },
  { key: "year",  label: "Cette année" },
];

function getDateRange(period) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const end = fmt(now);
  if (period === "today") return { start: end, end };
  if (period === "week") {
    const d = new Date(now); d.setDate(d.getDate() - 6);
    return { start: fmt(d), end };
  }
  if (period === "month") {
    return { start: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`, end };
  }
  return { start: `${now.getFullYear()}-01-01`, end };
}

function TrendBadge({ value }) {
  if (value == null) return null;
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-bold ${
      up ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
    }`}>
      {up ? <MdTrendingUp className="h-3 w-3" /> : <MdTrendingDown className="h-3 w-3" />}
      {Math.abs(value)}%
    </span>
  );
}

export default function Stats() {
  const { restaurantId } = useAuth();
  const [period, setPeriod]       = useState("month");
  const [dashData, setDashData]   = useState(null);
  const [menuData, setMenuData]   = useState(null);
  const [loading, setLoading]     = useState(true);

  const load = useCallback(() => {
    if (!restaurantId) return;
    setLoading(true);
    const { start, end } = getDateRange(period);
    Promise.all([
      getDashboardStats(restaurantId, start, end).catch(() => null),
      getMenuAnalytics(restaurantId, start, end).catch(() => null),
    ]).then(([dash, menu]) => {
      setDashData(dash?.data || dash || null);
      setMenuData(menu?.data || menu || null);
    }).finally(() => setLoading(false));
  }, [restaurantId, period]);

  useEffect(() => { load(); }, [load]);

  // ── Dashboard KPIs ─────────────────────────────────────────────────────────
  const revenue     = dashData?.revenue        || dashData?.total_revenue       || 0;
  const orderCount  = dashData?.orders         || dashData?.order_count         || 0;
  const avgBasket   = dashData?.average_basket || dashData?.average_order_value || 0;
  const cancelRate  = dashData?.cancellation_rate || 0;
  const avgRating   = dashData?.average_rating || 0;
  const avgPrepTime = dashData?.average_preparation_time || dashData?.preparation_time || 0;

  // ── Menu analytics ─────────────────────────────────────────────────────────
  const summary      = menuData?.summary          || {};
  const topProducts  = menuData?.top_products     || [];
  const allProducts  = menuData?.all_products     || topProducts;
  const categories   = menuData?.category_breakdown || [];
  const insights     = menuData?.category_insights  || [];
  const suggestions  = menuData?.optimization_suggestions || [];

  // Bar chart data (top products by quantity sold)
  const chartProducts = topProducts.slice(0, 8);
  const barChartOptions = {
    chart:       { toolbar: { show: false } },
    xaxis:       { categories: chartProducts.map((p) => p.name?.slice(0, 10) || ""), labels: { style: { fontSize: "10px" } } },
    colors:      ["#FF5A00"],
    dataLabels:  { enabled: false },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "55%" } },
    tooltip:     { y: { formatter: (v) => `${v} vendus` } },
  };

  return (
    <div className="mt-3">
      {/* Period selector */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <MdBarChart className="h-7 w-7 text-brand-500" />
          Statistiques
        </h2>
        <div className="flex gap-1 bg-gray-50 dark:bg-navy-700 rounded-xl p-1">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors ${
                period === p.key
                  ? "bg-brand-500 text-white"
                  : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-600"
              }`}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : (
        <>
          {/* ── Order KPI widgets (from dashboard stats) ── */}
          <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <Widget icon={<MdAttachMoney className="h-7 w-7" />}  title="Revenus"           subtitle={`${Number(revenue).toFixed(0)} TND`} />
            <Widget icon={<MdShoppingCart className="h-6 w-6" />} title="Commandes"         subtitle={String(orderCount)} />
            <Widget icon={<MdBarChart className="h-7 w-7" />}     title="Panier moyen"      subtitle={`${Number(avgBasket).toFixed(0)} TND`} />
            <Widget icon={<MdTrendingUp className="h-6 w-6" />}   title="Taux annulation"   subtitle={`${Number(cancelRate).toFixed(1)}%`} />
            <Widget icon={<MdTimer className="h-6 w-6" />}        title="Préparation moy."  subtitle={avgPrepTime ? `${Math.round(avgPrepTime)} min` : "—"} />
            <Widget icon={<MdStar className="h-6 w-6" />}         title="Note moyenne"      subtitle={avgRating ? `${Number(avgRating).toFixed(1)} ★` : "—"} />
          </div>

          {/* ── Menu summary KPIs (from menu analytics) ── */}
          {(summary.total_products_sold != null || summary.total_menu_revenue != null) && (
            <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
              {[
                { label: "Produits vendus",     value: summary.total_products_sold   ?? "—", color: "bg-brand-50 text-brand-500"  },
                { label: "CA menu",             value: summary.total_menu_revenue != null ? `${Number(summary.total_menu_revenue).toFixed(0)} TND` : "—", color: "bg-green-50 text-green-500" },
                { label: "Produits commandés",  value: summary.unique_products_ordered ?? "—", color: "bg-blue-50 text-blue-500"   },
                { label: "Articles/commande",   value: summary.avg_items_per_order    != null ? Number(summary.avg_items_per_order).toFixed(1) : "—", color: "bg-amber-50 text-amber-500" },
              ].map((item) => (
                <Card key={item.label} extra="p-4 flex items-center gap-4">
                  <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${item.color}`}>
                    <MdRestaurantMenu />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className="text-xl font-bold text-navy-700 dark:text-white">{item.value}</p>
                  </div>
                </Card>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2 mb-5">
            {/* Top products bar chart */}
            <Card extra="p-5">
              <h3 className="font-bold text-navy-700 dark:text-white mb-4">Top produits vendus</h3>
              {chartProducts.length > 0 ? (
                <BarChart
                  chartData={[{ name: "Qté vendue", data: chartProducts.map((p) => p.quantity_sold || 0) }]}
                  chartOptions={barChartOptions}
                />
              ) : (
                <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                  Pas de données produits pour cette période
                </div>
              )}
            </Card>

            {/* Category breakdown */}
            <Card extra="p-5">
              <h3 className="font-bold text-navy-700 dark:text-white mb-4 flex items-center gap-2">
                <MdCategory className="h-5 w-5 text-brand-500" />
                Répartition par catégorie
              </h3>
              {categories.length > 0 ? (
                <div className="space-y-3">
                  {categories.map((cat, i) => {
                    const totalRevenue = categories.reduce((s, c) => s + Number(c.revenue || 0), 0);
                    const pct = totalRevenue > 0 ? Math.round((Number(cat.revenue) / totalRevenue) * 100) : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-navy-700 dark:text-white">{cat.name}</span>
                          <span className="text-gray-500">{Number(cat.revenue || 0).toFixed(0)} TND · {cat.quantity_sold} vendus</span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 dark:bg-navy-700 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-brand-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                  Pas de données catégories
                </div>
              )}
            </Card>
          </div>

          {/* Top products detailed table */}
          {allProducts.length > 0 && (
            <Card extra="p-5 mb-5">
              <h3 className="font-bold text-navy-700 dark:text-white mb-4">Détail des produits</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-navy-700">
                      <th className="pb-2 text-left text-xs font-semibold text-gray-400 w-8">#</th>
                      <th className="pb-2 text-left text-xs font-semibold text-gray-400">Produit</th>
                      <th className="pb-2 text-left text-xs font-semibold text-gray-400">Catégorie</th>
                      <th className="pb-2 text-right text-xs font-semibold text-gray-400">Qté</th>
                      <th className="pb-2 text-right text-xs font-semibold text-gray-400">Commandes</th>
                      <th className="pb-2 text-right text-xs font-semibold text-gray-400">Clients uniques</th>
                      <th className="pb-2 text-right text-xs font-semibold text-gray-400">Revenus</th>
                      <th className="pb-2 text-right text-xs font-semibold text-gray-400">Tendance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allProducts.map((p, i) => (
                      <tr key={p.id || i} className="border-b border-gray-50 dark:border-navy-700 last:border-0">
                        <td className="py-2.5 text-gray-400 font-bold">{i + 1}</td>
                        <td className="py-2.5 font-medium text-navy-700 dark:text-white">{p.name}</td>
                        <td className="py-2.5 text-xs text-gray-500">{p.category || "—"}</td>
                        <td className="py-2.5 text-right text-gray-700 dark:text-gray-300 font-semibold">{p.quantity_sold ?? "—"}</td>
                        <td className="py-2.5 text-right text-gray-500">{p.order_count ?? "—"}</td>
                        <td className="py-2.5 text-right text-gray-500">{p.unique_customers ?? "—"}</td>
                        <td className="py-2.5 text-right font-semibold text-navy-700 dark:text-white">
                          {p.revenue != null ? `${Number(p.revenue).toFixed(0)} TND` : "—"}
                        </td>
                        <td className="py-2.5 text-right">
                          <TrendBadge value={p.trend_percentage} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Category insights */}
          {insights.length > 0 && (
            <Card extra="p-5 mb-5">
              <h3 className="font-bold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                <MdLightbulb className="h-5 w-5 text-amber-400" />
                Insights catégories
              </h3>
              <div className="space-y-2">
                {insights.map((ins, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 px-4 py-3">
                    <MdLightbulb className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-navy-700 dark:text-white">{ins.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{ins.recommendation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Optimization suggestions */}
          {suggestions.length > 0 && (
            <Card extra="p-5">
              <h3 className="font-bold text-navy-700 dark:text-white mb-3 flex items-center gap-2">
                <MdTrendingUp className="h-5 w-5 text-brand-500" />
                Suggestions d'optimisation
              </h3>
              <div className="space-y-2">
                {suggestions.map((s, i) => {
                  const priorityColor = s.priority === "high"
                    ? "bg-red-50 border-red-200 text-red-600"
                    : s.priority === "medium"
                    ? "bg-orange-50 border-orange-200 text-orange-600"
                    : "bg-blue-50 border-blue-200 text-blue-600";
                  return (
                    <div key={i} className={`rounded-xl border px-4 py-3 ${priorityColor}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold uppercase">{s.type}</span>
                        <span className="text-xs opacity-70">· priorité {s.priority}</span>
                      </div>
                      <p className="text-sm">{s.message}</p>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
